/**
 * API client.
 *
 * Same-origin by design: in production the Worker serves /api on the same
 * hostname as the Pages app, and in development Vite proxies /api to
 * wrangler dev. So no base URL and no CORS.
 */

export interface ApiErrorBody {
  error: string;
  message: string;
  fields?: Record<string, string>;
  positions?: number[];
  /** Seconds until a 429 clears. */
  retry_after?: number;
}

/** A structured failure from the API. `fields` drives inline form errors. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.body = body;
  }
}

/** The network did not reach the API at all. Distinct from a 4xx: retrying may work. */
export class NetworkError extends Error {
  constructor() {
    super('Could not reach the server.');
    this.name = 'NetworkError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    });
  } catch {
    throw new NetworkError();
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    const parsed =
      body && typeof body === 'object'
        ? (body as ApiErrorBody)
        : { error: 'internal_error', message: 'Something went wrong.' };
    throw new ApiRequestError(res.status, parsed);
  }

  return body as T;
}

export interface CreatedResponse {
  response_id: string;
  total_questions: number;
  instrument_version: string;
}

export interface Progress {
  status: 'in_progress' | 'complete';
  answered_positions: number[];
  answered: number;
  total_questions: number;
  next_position: number | null;
}

export function createParticipant(input: {
  first_name: string;
  last_name: string;
  email: string;
  consent: boolean;
  source: string | null;
}): Promise<CreatedResponse> {
  // Spec FR9: the ?src= tag rides along from the landing URL.
  const query = input.source ? `?src=${encodeURIComponent(input.source)}` : '';
  return request<CreatedResponse>(`/api/participants${query}`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function getProgress(responseId: string): Promise<Progress> {
  return request<Progress>(`/api/responses/${responseId}/progress`);
}

export function saveAnswer(
  responseId: string,
  qsPosition: number,
  input: { most_word: string; least_word: string; time_spent_ms: number | null },
): Promise<{ ok: true; answered: number }> {
  return request(`/api/responses/${responseId}/answers/${qsPosition}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export function completeResponse(responseId: string): Promise<{ ok: true; status: 'complete' }> {
  return request(`/api/responses/${responseId}/complete`, { method: 'POST' });
}
